/** Tasks hooks with mock fallback. Client names resolved from the clients cache. */

import { useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { NOTIFICATIONS_QUERY_PREFIX, TASKS_QUERY_PREFIX, queryKeys } from "@/lib/query-client";
import type { Task, TaskStatus } from "@/types";
import { useAuthStore } from "@/features/auth/auth.store";
import { useClients } from "@/features/clients/clients.hooks";
import { tasksApi } from "./tasks.api";
import { toTask, toTaskWriteBody, toMockTask, type TaskInput } from "./tasks.adapter";
import { useTasksStore } from "./tasks.store";

const LIST_PARAMS = { limit: 100, sortBy: "createdAt", sortOrder: "desc" as const };

export type DataSource = "api" | "mock";

export interface UseTasksResult {
  tasks: Task[];
  isLoading: boolean;
  isError: boolean;
  isFetching: boolean;
  source: DataSource;
  error: unknown;
  refetch: () => void;
}

export function useTasks(): UseTasksResult {
  const authed = useAuthStore((s) => s.status === "authenticated");
  const mockActive = useTasksStore((s) => s.active);
  const { clients } = useClients();
  const nameById = useMemo(() => new Map(clients.map((c) => [c.id, c.name])), [clients]);

  const query = useQuery({
    queryKey: queryKeys.tasksList(LIST_PARAMS),
    queryFn: () => tasksApi.list(LIST_PARAMS),
    enabled: authed,
  });

  if (!authed) return { tasks: mockActive, isLoading: false, isError: false, isFetching: false, source: "mock", error: null, refetch: query.refetch };
  if (query.data) return { tasks: query.data.data.map((t) => toTask(t, nameById)), isLoading: false, isError: false, isFetching: query.isFetching, source: "api", error: null, refetch: query.refetch };
  if (query.isError) return { tasks: mockActive, isLoading: false, isError: true, isFetching: false, source: "mock", error: query.error, refetch: query.refetch };
  return { tasks: [], isLoading: query.isLoading, isError: false, isFetching: query.isFetching, source: "api", error: null, refetch: query.refetch };
}

export function useMyTasks(): UseTasksResult {
  const authed = useAuthStore((s) => s.status === "authenticated");
  const mockActive = useTasksStore((s) => s.active);
  const { clients } = useClients();
  const nameById = useMemo(() => new Map(clients.map((c) => [c.id, c.name])), [clients]);

  const query = useQuery({
    queryKey: queryKeys.myTasks,
    queryFn: () => tasksApi.my(),
    enabled: authed,
  });

  if (!authed) return { tasks: mockActive, isLoading: false, isError: false, isFetching: false, source: "mock", error: null, refetch: query.refetch };
  if (query.data) return { tasks: query.data.data.map((t) => toTask(t, nameById)), isLoading: false, isError: false, isFetching: query.isFetching, source: "api", error: null, refetch: query.refetch };
  if (query.isError) return { tasks: [], isLoading: false, isError: true, isFetching: false, source: "api", error: query.error, refetch: query.refetch };
  return { tasks: [], isLoading: query.isLoading, isError: false, isFetching: query.isFetching, source: "api", error: null, refetch: query.refetch };
}

function useInvalidateTasks() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: TASKS_QUERY_PREFIX });
}

function useInvalidateNotifications() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: NOTIFICATIONS_QUERY_PREFIX });
}

export function useCreateTask() {
  const authed = useAuthStore((s) => s.status === "authenticated");
  const add = useTasksStore((s) => s.add);
  const invalidate = useInvalidateTasks();
  const invalidateNotifications = useInvalidateNotifications();
  return useMutation({
    mutationFn: async (input: TaskInput) => {
      if (authed) return tasksApi.create(toTaskWriteBody(input));
      const mock = toMockTask(input);
      add(mock);
      return mock;
    },
    onSuccess: () => {
      if (authed) {
        void invalidate();
        void invalidateNotifications();
      }
    },
  });
}

export function useUpdateTask() {
  const authed = useAuthStore((s) => s.status === "authenticated");
  const update = useTasksStore((s) => s.update);
  const invalidate = useInvalidateTasks();
  const invalidateNotifications = useInvalidateNotifications();
  return useMutation({
    mutationFn: async ({ id, input }: { id: string; input: TaskInput }) => {
      if (authed) return tasksApi.update(id, toTaskWriteBody(input));
      update(id, { title: input.title, priority: input.priority, status: input.status, dueDate: input.dueDate || undefined });
      return null;
    },
    onSuccess: () => {
      if (authed) {
        void invalidate();
        void invalidateNotifications();
      }
    },
  });
}

export function useArchiveTask() {
  const authed = useAuthStore((s) => s.status === "authenticated");
  const archive = useTasksStore((s) => s.archive);
  const invalidate = useInvalidateTasks();
  return useMutation({
    mutationFn: async (id: string) => {
      if (authed) return tasksApi.archive(id);
      archive(id);
      return null;
    },
    onSuccess: () => {
      if (authed) void invalidate();
    },
  });
}

/** Lightweight hook for member status-only updates. Optionally includes a progress override. */
export function useUpdateTaskStatus() {
  const authed = useAuthStore((s) => s.status === "authenticated");
  const update = useTasksStore((s) => s.update);
  const invalidate = useInvalidateTasks();
  return useMutation({
    mutationFn: async ({ id, status, progress }: { id: string; status: TaskStatus; progress?: number }) => {
      const body: Record<string, unknown> = { status };
      if (progress !== undefined) body.progress = progress;
      if (authed) return tasksApi.update(id, body);
      update(id, { status, ...(progress !== undefined ? { progress } : {}) });
      return null;
    },
    onSuccess: () => {
      if (authed) void invalidate();
    },
  });
}

/** Sends only { progress } — used by member inline progress input. */
export function useUpdateProgress() {
  const authed = useAuthStore((s) => s.status === "authenticated");
  const update = useTasksStore((s) => s.update);
  const invalidate = useInvalidateTasks();
  return useMutation({
    mutationFn: async ({ id, progress }: { id: string; progress: number }) => {
      if (authed) return tasksApi.update(id, { progress });
      update(id, { progress });
      return null;
    },
    onSuccess: () => {
      if (authed) void invalidate();
    },
  });
}

export function useRestoreTask() {
  const authed = useAuthStore((s) => s.status === "authenticated");
  const restore = useTasksStore((s) => s.restore);
  const invalidate = useInvalidateTasks();
  return useMutation({
    mutationFn: async (id: string) => {
      if (authed) return tasksApi.restore(id);
      restore(id);
      return null;
    },
    onSuccess: () => {
      if (authed) void invalidate();
    },
  });
}

const ARCHIVED_PARAMS = { archived: true, limit: 100, sortBy: "createdAt", sortOrder: "desc" as const };

export function useArchivedTasks() {
  const authed = useAuthStore((s) => s.status === "authenticated");
  const { clients } = useClients();
  const nameById = useMemo(() => new Map(clients.map((c) => [c.id, c.name])), [clients]);
  const query = useQuery({
    queryKey: queryKeys.tasksList(ARCHIVED_PARAMS),
    queryFn: () => tasksApi.list(ARCHIVED_PARAMS),
    enabled: authed,
  });
  if (query.data) return { tasks: query.data.data.map((t) => toTask(t, nameById)), isLoading: false, isError: false, refetch: query.refetch };
  if (query.isError) return { tasks: [] as Task[], isLoading: false, isError: true, refetch: query.refetch };
  return { tasks: [] as Task[], isLoading: query.isLoading, isError: false, refetch: query.refetch };
}

export function useTask(id: string): { task: Task | null; isLoading: boolean; isError: boolean; error: unknown } {
  const authed = useAuthStore((s) => s.status === "authenticated");
  const { clients } = useClients();
  const nameById = useMemo(() => new Map(clients.map((c) => [c.id, c.name])), [clients]);

  const query = useQuery({
    queryKey: queryKeys.task(id),
    queryFn: () => tasksApi.findById(id),
    enabled: authed && !!id,
  });

  if (query.data) return { task: toTask(query.data, nameById), isLoading: false, isError: false, error: null };
  if (query.isError) return { task: null, isLoading: false, isError: true, error: query.error };
  return { task: null, isLoading: query.isLoading, isError: false, error: null };
}
